<script lang="ts">
    import ReviewsAverageRatingStars from "@apps/reviews/components/ReviewsAverageRatingStars/ReviewsAverageRatingStars.svelte";
    import ReviewsTheme from "@apps/reviews/components/ReviewsTheme/ReviewsTheme.svelte";
    import ReviewsTooltipContent from "@apps/reviews/components/ReviewsTooltip/ReviewsTooltipContent.svelte";
    import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
    import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
    import { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import { filter, map, switchMap, tap } from "rxjs";
    import { onMount } from "svelte";

    import BaseSkeleton from "@components/skeletons/BaseSkeleton.svelte";

    import ListingProductInfoQuery from "../queries/ListingProductInfoQuery";
    import ListingProductInfoService from "../services/ListingProductInfoService";

    export let reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    export let listingProductInfoService: ListingProductInfoService;
    export let listingProductInfoQuery: ListingProductInfoQuery;
    export let productIdManager: ProductIdManagerInterface;
    export let showReviewsTooltip: boolean;
    export let gwI18n: GwI18n;
    export let afterWidgetInject: () => void;

    const reviewsBranding$ =
        reviewsStorefrontValuesService.selectReviewsBrandingOptions();

    const productId$ = productIdManager.productId$;

    const info$ = productId$.pipe(
        filter((productId): productId is number => productId !== null),
        switchMap((productId) =>
            listingProductInfoQuery.selectEntity(productId).pipe(
                map((info) => {
                    // If productId exists but data is missing, return empty object with zero values
                    if (!info) {
                        return {
                            productId,
                            avgRate: 0,
                            reviewCount: 0,
                            starsDistribution: [],
                        };
                    }
                    return info;
                })
            )
        )
    );
    const loading$ = listingProductInfoQuery.loading$;
    const error$ = listingProductInfoQuery.error$;

    const basedOnReviewsText$ = info$.pipe(
        switchMap((info) =>
            gwI18n.selectT("reviews.AverageReviewsWidget", "based_on_reviews", {
                count: info?.reviewCount || 0,
            })
        )
    );

    onMount(() => {
        const productIdSubscription = productId$
            .pipe(
                filter((productId): productId is number => productId !== null),
                tap((productId) => {
                    listingProductInfoService.getBufferedListingProductInfo(
                        productId,
                        showReviewsTooltip ? true : undefined
                    );
                })
            )
            .subscribe();

        afterWidgetInject();

        return () => {
            productIdSubscription.unsubscribe();
        };
    });

    $: info = $info$;
</script>

{#if $reviewsBranding$}
    <ReviewsTheme branding={$reviewsBranding$}>
        <div
            class="gw-rv-listing-average-reviews"
            data-gw-rating-count={info?.reviewCount}
            data-gw-rating-avg={info?.avgRate}
        >
            {#if !$error$}
                {#if $loading$}
                    <BaseSkeleton width="100px" height="20px" />
                    <BaseSkeleton width="30px" height="20px" />
                {:else if info}
                    {#if showReviewsTooltip}
                        <ReviewsTooltipContent
                            allReviewsCount={info?.reviewCount || 0}
                            averageReviewsRating={info?.avgRate}
                            ratesInfo={info?.starsDistribution || []}
                            basedOnReviewsText={$basedOnReviewsText$}
                        >
                            <ReviewsAverageRatingStars
                                rating={info.avgRate || 0}
                                starsSize={20}
                            />
                            <span class="gw-rv-listing-average-reviews__count">
                                ({info.reviewCount})
                            </span>
                        </ReviewsTooltipContent>
                    {:else}
                        <ReviewsAverageRatingStars
                            rating={info.avgRate || 0}
                            starsSize={20}
                        />
                        <span class="gw-rv-listing-average-reviews__count">
                            ({info.reviewCount})
                        </span>
                    {/if}
                {/if}
            {/if}
        </div>
    </ReviewsTheme>
{/if}

<style lang="postcss" global>
    @import "../../../../../themes/default/default.css";
    @import "../../../../../themes/globals.css";
    .gw-rv-listing-average-reviews {
        position: relative;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        column-gap: 0.625em;
        padding: 0.25em 0;
        z-index: 1;

        &__count {
            line-height: 1;
        }
    }
</style>
